import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import HostelGateScanner from "./Security/QrScanner";
import HostelAIAssistant from "./AIAssistant";
import HostelReports from "./Reports";
import {
  FaBuilding,
  FaBed,
  FaUserCheck,
  FaIdCard,
  FaUtensils,
  FaTools,
  FaChartPie,
  FaBullhorn,
  FaUserShield,
  FaQrcode,
  FaPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUsers,
  FaRobot,
  FaFileExcel,
  FaExchangeAlt,
  FaSearch,
  FaFilter,
  FaPlaneDeparture,
  FaCalendarAlt,
  FaCheckDouble,
  FaExclamationTriangle,
  FaRedo,
  FaGraduationCap,
  FaLayerGroup,
  FaUserGraduate,
} from "react-icons/fa";

const HostelHome = () => {
  const router = useLocation();
  const navigate = useNavigate();
  const [load, setLoad] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("Dashboard");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // State Data
  const [analytics, setAnalytics] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState("all");
  const [rooms, setRooms] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [gatePasses, setGatePasses] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [messMenus, setMessMenus] = useState([]);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [showEditRoomModal, setShowEditRoomModal] = useState(false);
  const [selectedRoomForEdit, setSelectedRoomForEdit] = useState(null);

  const [showAddBlockModal, setShowAddBlockModal] = useState(false);
  const [showEditBlockModal, setShowEditBlockModal] = useState(false);
  const [selectedBlockForEdit, setSelectedBlockForEdit] = useState(null);

  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showVacateModal, setShowVacateModal] = useState(false);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [selectedBedForDetail, setSelectedBedForDetail] = useState(null);
  const [selectedAllocForVacate, setSelectedAllocForVacate] = useState(null);

  // Form states
  const [newBlock, setNewBlock] = useState({ blockName: "", code: "", type: "Boys", totalFloors: 3, curfewTime: "21:30", caretakerName: "", caretakerPhone: "", description: "" });
  const [editBlockForm, setEditBlockForm] = useState({ blockName: "", code: "", type: "Boys", totalFloors: 3, curfewTime: "21:30", caretakerName: "", caretakerPhone: "", description: "", status: "Active" });

  const [newRoom, setNewRoom] = useState({ blockId: "", roomNumber: "", floorNumber: 1, roomType: "Triple", capacity: 3, rentPerSemester: 25000, amenities: ["Ceiling Fan", "Study Table", "Wi-Fi"], notes: "" });
  const [editRoomForm, setEditRoomForm] = useState({ roomNumber: "", floorNumber: 1, roomType: "Triple", capacity: 3, rentPerSemester: 25000, amenities: "", status: "Available", notes: "" });

  const [allocForm, setAllocForm] = useState({ enrollmentNo: "", bedId: "", roomRent: 25000, messFee: 0, cautionDeposit: 0, paidAmount: 0 });
  const [swapForm, setSwapForm] = useState({ student1Enroll: "", student2Enroll: "", reason: "" });
  const [vacateForm, setVacateForm] = useState({ damagesCost: 0, messRebateDeduction: 0, remarks: "" });
  const [noticeForm, setNoticeForm] = useState({ title: "", description: "", targetAudience: "All Hostellers", priority: "Normal" });

  // Roll call attendance state & filters
  const [rollCallSheet, setRollCallSheet] = useState([]);
  const [rollCallDate, setRollCallDate] = useState(new Date().toISOString().split("T")[0]);
  const [rollCallBlock, setRollCallBlock] = useState("all");
  const [rollCallFloor, setRollCallFloor] = useState("all");
  const [rollCallStatusFilter, setRollCallStatusFilter] = useState("all");
  const [rollCallSearch, setRollCallSearch] = useState("");
  const [rollCallLoading, setRollCallLoading] = useState(false);
  const [isSavingRollCall, setIsSavingRollCall] = useState(false);

  // Allocation filters and student lookup state
  const [allocSemesterFilter, setAllocSemesterFilter] = useState("all");
  const [allocRegulationFilter, setAllocRegulationFilter] = useState("all");
  const [allocBatchFilter, setAllocBatchFilter] = useState("all");
  const [allocBranchFilter, setAllocBranchFilter] = useState("all");
  const [allocStatusFilter, setAllocStatusFilter] = useState("all");
  const [allocSearch, setAllocSearch] = useState("");
  const [studentLookupLoading, setStudentLookupLoading] = useState(false);
  const [studentLookupData, setStudentLookupData] = useState(null);
  const [studentLookupError, setStudentLookupError] = useState("");

  // Dynamic lists for regulations and batches
  const availableAllocRegulations = useMemo(() => {
    const set = new Set();
    allocations.forEach((a) => {
      if (a.studentId?.regulation) set.add(a.studentId.regulation.toUpperCase());
    });
    ["R22", "R20", "R18", "R16"].forEach((r) => set.add(r));
    return Array.from(set).sort();
  }, [allocations]);

  const availableAllocBatches = useMemo(() => {
    const set = new Set();
    allocations.forEach((a) => {
      if (a.studentId?.batch) set.add(String(a.studentId.batch));
    });
    ["2026", "2025", "2024", "2023", "2022", "2021", "2020"].forEach((b) => set.add(b));
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [allocations]);

  // Filtered allocations
  const filteredAllocations = useMemo(() => {
    return allocations.filter((alloc) => {
      const student = alloc.studentId || {};
      // Semester
      if (allocSemesterFilter !== "all") {
        if (String(student.semester) !== String(allocSemesterFilter)) return false;
      }
      // Regulation
      if (allocRegulationFilter !== "all") {
        if ((student.regulation || "").toUpperCase() !== allocRegulationFilter.toUpperCase()) return false;
      }
      // Batch
      if (allocBatchFilter !== "all") {
        if (String(student.batch || "") !== String(allocBatchFilter)) return false;
      }
      // Branch
      if (allocBranchFilter !== "all") {
        if ((student.branch || "").toUpperCase() !== allocBranchFilter.toUpperCase()) return false;
      }
      // Fee Status / Allocation Status
      if (allocStatusFilter !== "all") {
        const feeStatusMatch = (alloc.feeStructure?.feeStatus || "").toLowerCase() === allocStatusFilter.toLowerCase();
        const allocStatusMatch = (alloc.status || "").toLowerCase() === allocStatusFilter.toLowerCase();
        if (!feeStatusMatch && !allocStatusMatch) return false;
      }
      // Search Query
      if (allocSearch.trim()) {
        const q = allocSearch.toLowerCase().trim();
        const sName = `${student.firstName || ""} ${student.lastName || ""}`.toLowerCase();
        const sEnroll = (student.enrollmentNo || "").toLowerCase();
        const sBranch = (student.branch || "").toLowerCase();
        const sReg = (student.regulation || "").toLowerCase();
        const sBlock = (alloc.blockId?.blockName || "").toLowerCase();
        const sRoom = (alloc.roomId?.roomNumber || "").toLowerCase();
        const sBed = (alloc.bedId?.bedCode || "").toLowerCase();
        return (
          sName.includes(q) ||
          sEnroll.includes(q) ||
          sBranch.includes(q) ||
          sReg.includes(q) ||
          sBlock.includes(q) ||
          sRoom.includes(q) ||
          sBed.includes(q)
        );
      }
      return true;
    });
  }, [allocations, allocSemesterFilter, allocRegulationFilter, allocBatchFilter, allocBranchFilter, allocStatusFilter, allocSearch]);

  const lookupStudent = async (enrollmentNo) => {
    if (!enrollmentNo || !enrollmentNo.trim()) {
      setStudentLookupData(null);
      setStudentLookupError("");
      return;
    }
    try {
      setStudentLookupLoading(true);
      setStudentLookupError("");
      const activeToken = localStorage.getItem("token") || token;
      const res = await axios.post(
        `${baseApiURL()}/student/details/getDetails`,
        { enrollmentNo: enrollmentNo.trim() },
        { headers: { Authorization: `Bearer ${activeToken}` } }
      );
      const student = res.data?.user?.[0];
      if (student) {
        setStudentLookupData(student);
        setStudentLookupError("");
      } else {
        setStudentLookupData(null);
        setStudentLookupError(`No registered student found with enrollment "${enrollmentNo.trim()}".`);
      }
    } catch (err) {
      setStudentLookupData(null);
      setStudentLookupError("Failed to lookup student details.");
    } finally {
      setStudentLookupLoading(false);
    }
  };

  const token = localStorage.getItem("token");

  useEffect(() => {
    const activeToken = localStorage.getItem("token");
    if (!router.state && !activeToken) {
      navigate("/");
    } else {
      setLoad(true);
    }
  }, [navigate, router.state]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };

      const [
        analyticsRes,
        blocksRes,
        allocsRes,
        gpRes,
        compRes,
        messRes,
        noticeRes,
      ] = await Promise.all([
        axios.get(`${baseApiURL()}/hostel/analytics`, { headers }).catch(() => ({ data: { analytics: null } })),
        axios.get(`${baseApiURL()}/hostel/blocks`, { headers }).catch(() => ({ data: { blocks: [] } })),
        axios.get(`${baseApiURL()}/hostel/allocations`, { headers }).catch(() => ({ data: { allocations: [] } })),
        axios.get(`${baseApiURL()}/hostel/gatepass`, { headers }).catch(() => ({ data: { gatePasses: [] } })),
        axios.get(`${baseApiURL()}/hostel/complaints`, { headers }).catch(() => ({ data: { complaints: [] } })),
        axios.get(`${baseApiURL()}/hostel/mess/menu`, { headers }).catch(() => ({ data: { menus: [] } })),
        axios.get(`${baseApiURL()}/hostel/notices`, { headers }).catch(() => ({ data: { notices: [] } })),
      ]);

      setAnalytics(analyticsRes.data?.analytics);
      setBlocks(blocksRes.data?.blocks || []);
      setAllocations(allocsRes.data?.allocations || []);
      setGatePasses(gpRes.data?.gatePasses || []);
      setComplaints(compRes.data?.complaints || []);
      setMessMenus(messRes.data?.menus || []);
      setNotices(noticeRes.data?.notices || []);

      if (blocksRes.data?.blocks?.length > 0) {
        fetchRooms(blocksRes.data.blocks[0]._id);
        setSelectedBlock(blocksRes.data.blocks[0]._id);
      }
    } catch (err) {
      console.error("Fetch Data Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async (bId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${baseApiURL()}/hostel/rooms/${bId || "all"}`, { headers });
      setRooms(res.data?.rooms || []);
    } catch (err) {
      console.error("Fetch Rooms Error:", err);
    }
  };

  const fetchRollCall = async (targetBlock = rollCallBlock, targetFloor = rollCallFloor, targetDate = rollCallDate) => {
    try {
      setRollCallLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const blockQuery = targetBlock && targetBlock !== "all" ? targetBlock : "all";
      const floorQuery = targetFloor && targetFloor !== "all" ? targetFloor : "";
      const res = await axios.get(
        `${baseApiURL()}/hostel/attendance/sheet?blockId=${blockQuery}&floorNumber=${floorQuery}&date=${targetDate}`,
        { headers }
      );
      setRollCallSheet(res.data?.sheet || []);
    } catch (err) {
      toast.error("Failed to load roll call sheet");
    } finally {
      setRollCallLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMenu === "Roll Call") {
      fetchRollCall(rollCallBlock, rollCallFloor, rollCallDate);
    }
  }, [selectedMenu, rollCallBlock, rollCallFloor, rollCallDate]);

  // Handlers
  const handleCreateBlock = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${baseApiURL()}/hostel/blocks`, newBlock, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        toast.success("Hostel Block created!");
        setShowAddBlockModal(false);
        fetchInitialData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create block");
    }
  };

  const handleOpenEditBlock = (block) => {
    setSelectedBlockForEdit(block);
    setEditBlockForm({
      blockName: block.blockName || "",
      code: block.code || "",
      type: block.type || "Boys",
      totalFloors: block.totalFloors || 3,
      curfewTime: block.curfewTime || "21:30",
      caretakerName: block.caretakerName || "",
      caretakerPhone: block.caretakerPhone || "",
      description: block.description || "",
      status: block.status || "Active",
    });
    setShowEditBlockModal(true);
  };

  const handleUpdateBlock = async (e) => {
    e.preventDefault();
    if (!selectedBlockForEdit) return;
    try {
      const res = await axios.put(
        `${baseApiURL()}/hostel/blocks/${selectedBlockForEdit._id}`,
        editBlockForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success("Hostel Block updated successfully!");
        setShowEditBlockModal(false);
        fetchInitialData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update block");
    }
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${baseApiURL()}/hostel/rooms`, newRoom, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        toast.success(res.data.message || "Room created successfully!");
        setShowAddRoomModal(false);
        fetchRooms(selectedBlock);
        fetchInitialData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create room");
    }
  };

  const handleOpenEditRoom = (room) => {
    setSelectedRoomForEdit(room);
    setEditRoomForm({
      roomNumber: room.roomNumber || "",
      floorNumber: room.floorNumber || 1,
      roomType: room.roomType || "Triple",
      capacity: room.capacity || 3,
      rentPerSemester: room.rentPerSemester || 25000,
      amenities: Array.isArray(room.amenities) ? room.amenities.join(", ") : "",
      status: room.status || "Available",
      notes: room.notes || "",
    });
    setShowEditRoomModal(true);
  };

  const handleUpdateRoom = async (e) => {
    e.preventDefault();
    if (!selectedRoomForEdit) return;
    try {
      const amenitiesArr = editRoomForm.amenities
        ? editRoomForm.amenities.split(",").map((s) => s.trim()).filter(Boolean)
        : [];

      const res = await axios.put(
        `${baseApiURL()}/hostel/rooms/${selectedRoomForEdit._id}`,
        {
          ...editRoomForm,
          amenities: amenitiesArr,
          capacity: Number(editRoomForm.capacity),
          rentPerSemester: Number(editRoomForm.rentPerSemester),
          floorNumber: Number(editRoomForm.floorNumber),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success("Room updated successfully!");
        setShowEditRoomModal(false);
        fetchRooms(selectedBlock);
        fetchInitialData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update room");
    }
  };

  const handleAllocateBed = async (e) => {
    e.preventDefault();
    try {
      const studentRes = await axios.post(`${baseApiURL()}/student/details/getDetails`, { enrollmentNo: allocForm.enrollmentNo }, { headers: { Authorization: `Bearer ${token}` } });
      const student = studentRes.data?.user?.[0];
      if (!student) {
        toast.error("Student with this Enrollment Number not found!");
        return;
      }

      const roomRent = Number(allocForm.roomRent || 0);
      const messFee = Number(allocForm.messFee || 0);
      const cautionDeposit = Number(allocForm.cautionDeposit || 0);
      const totalFee = roomRent + messFee + cautionDeposit;
      const paidAmount = Number(allocForm.paidAmount || 0);

      const res = await axios.post(
        `${baseApiURL()}/hostel/allocate`,
        {
          studentId: student._id,
          bedId: allocForm.bedId,
          feeStructure: {
            roomRent,
            messFee,
            cautionDeposit,
            totalFee,
            paidAmount,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success("Student successfully allocated to room & bed!");
        setShowAllocateModal(false);
        fetchRooms(selectedBlock);
        fetchInitialData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Allocation failed");
    }
  };

  const handleGatePassAction = async (passId, status, remarks = "") => {
    try {
      const res = await axios.put(
        `${baseApiURL()}/hostel/gatepass/${passId}`,
        { wardenApprovalStatus: status, wardenRemarks: remarks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(`Gate pass marked as ${status}`);
        fetchInitialData();
      }
    } catch (err) {
      toast.error("Failed to update gate pass");
    }
  };

  const handleSaveAttendance = async () => {
    try {
      setIsSavingRollCall(true);
      const records = rollCallSheet.map((item) => ({
        studentId: item.student?._id,
        blockId: item.block?._id,
        roomId: item.room?._id,
        floorNumber: item.room?.floorNumber || 0,
        status: item.status,
        remarks: item.remarks,
      }));

      const res = await axios.post(
        `${baseApiURL()}/hostel/attendance/save`,
        { date: rollCallDate, records, markedBy: "Chief Warden" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success(res.data.message || "Night roll call saved successfully!");
      }
    } catch (err) {
      toast.error("Failed to save night attendance");
    } finally {
      setIsSavingRollCall(false);
    }
  };

  const handleMarkAllStudents = (status) => {
    const updated = rollCallSheet.map((item) => {
      if (item.onGatePass && status === "Present") {
        return item; // preserve approved leave
      }
      return { ...item, status };
    });
    setRollCallSheet(updated);
    toast.success(`Marked all eligible hostellers as ${status}`);
  };

  const handleVacateSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAllocForVacate) return;
    try {
      const res = await axios.post(
        `${baseApiURL()}/hostel/vacate`,
        {
          allocationId: selectedAllocForVacate._id,
          damagesCost: Number(vacateForm.damagesCost),
          messRebateDeduction: Number(vacateForm.messRebateDeduction),
          remarks: vacateForm.remarks,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success(`Room vacated! Net refund: ₹${res.data.netRefundAmount}`);
        setShowVacateModal(false);
        fetchRooms(selectedBlock);
        fetchInitialData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to process vacating");
    }
  };

  const handleDeallocateBed = async (bedId) => {
    try {
      const res = await axios.put(
        `${baseApiURL()}/hostel/beds/${bedId}`,
        { deallocate: true, status: "Available" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success("Bed deallocated and marked Available!");
        setSelectedBedForDetail(null);
        fetchRooms(selectedBlock);
        fetchInitialData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to deallocate bed");
    }
  };

  const handleChangeBedStatus = async (bedId, newStatus) => {
    try {
      const res = await axios.put(
        `${baseApiURL()}/hostel/beds/${bedId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(`Bed marked as ${newStatus}`);
        setSelectedBedForDetail(null);
        fetchRooms(selectedBlock);
        fetchInitialData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update bed status");
    }
  };

  const handleCreateNotice = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${baseApiURL()}/hostel/notices`, noticeForm, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        toast.success("Notice published!");
        setShowNoticeModal(false);
        fetchInitialData();
      }
    } catch (err) {
      toast.error("Failed to publish notice");
    }
  };

  const renderContent = () => {
    switch (selectedMenu) {
      case "Dashboard":
        return (
          <div className="space-y-6">
            {/* Metric Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="flex justify-between items-center text-slate-500">
                  <span className="text-xs font-semibold uppercase tracking-wider">Total Bed Capacity</span>
                  <FaBed className="text-rose-500 text-lg" />
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-3xl font-black text-slate-800 dark:text-slate-100">
                    {analytics?.totalBeds || 0}
                  </span>
                  <span className="text-xs text-emerald-500 font-bold">
                    {analytics?.occupancyRate || 0}% Occupied
                  </span>
                </div>
                <div className="mt-2 text-xs text-slate-400">
                  🟢 {analytics?.occupiedBeds || 0} Occupied | ⚪ {analytics?.availableBeds || 0} Available
                </div>
              </div>

              <div className="p-5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="flex justify-between items-center text-slate-500">
                  <span className="text-xs font-semibold uppercase tracking-wider">Active Gate Passes</span>
                  <FaIdCard className="text-amber-500 text-lg" />
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-3xl font-black text-slate-800 dark:text-slate-100">
                    {analytics?.activeGatePasses || 0}
                  </span>
                  <span className="text-xs text-amber-500 font-bold">Currently Out</span>
                </div>
                <div className="mt-2 text-xs text-slate-400">
                  ⚠️ {analytics?.overdueGatePasses || 0} Overdue | ⏳ {analytics?.pendingGatePasses || 0} Pending
                </div>
              </div>

              <div className="p-5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="flex justify-between items-center text-slate-500">
                  <span className="text-xs font-semibold uppercase tracking-wider">Revenue & Fees</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                    Semester
                  </span>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
                    ₹{(analytics?.totalRevenueCollected || 0).toLocaleString()}
                  </span>
                </div>
                <div className="mt-2 text-xs text-rose-500 font-semibold">
                  ₹{(analytics?.totalDues || 0).toLocaleString()} Pending Dues
                </div>
              </div>

              <div className="p-5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="flex justify-between items-center text-slate-500">
                  <span className="text-xs font-semibold uppercase tracking-wider">Maintenance SLA</span>
                  <FaTools className="text-indigo-500 text-lg" />
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-3xl font-black text-slate-800 dark:text-slate-100">
                    {analytics?.openComplaints || 0}
                  </span>
                  <span className="text-xs text-slate-400">Active Tickets</span>
                </div>
                <div className="mt-2 text-xs text-rose-500 font-semibold">
                  🚨 {analytics?.criticalComplaints || 0} Critical Priority
                </div>
              </div>
            </div>

            {/* Blocks Overview & Capacity Progress */}
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                <FaBuilding className="text-rose-500" /> Hostel Blocks Occupancy Breakdown
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {analytics?.blockWiseStats?.map((b) => {
                  const occPct = b.totalCapacity > 0 ? Math.round((b.occupiedCount / b.totalCapacity) * 100) : 0;
                  return (
                    <div key={b._id} className="p-4 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-bold text-sm text-slate-800 dark:text-slate-100">{b.blockName}</h4>
                          <span className="text-[11px] text-slate-500 font-medium">
                            {b.type} Hostel • {b.totalFloors} Floors • Curfew {b.curfewTime}
                          </span>
                        </div>
                        <span className="text-xs font-bold px-2 py-1 bg-white dark:bg-slate-700 rounded-lg shadow-xs">
                          {occPct}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 rounded-full overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-rose-500 to-amber-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${occPct}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>{b.occupiedCount} Occupied</span>
                        <span>{b.totalCapacity - b.occupiedCount} Free ({b.totalCapacity} Total)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent Gate Passes & Maintenance Split */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-sm text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <FaIdCard className="text-amber-500" /> Recent Gate Passes
                  </h3>
                  <button onClick={() => setSelectedMenu("Gate Pass Center")} className="text-xs text-rose-500 hover:underline font-semibold cursor-pointer">
                    View All ({gatePasses.length})
                  </button>
                </div>
                <div className="space-y-2">
                  {gatePasses.slice(0, 4).map((gp) => (
                    <div key={gp._id} className="flex justify-between items-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 text-xs">
                      <div>
                        <div className="font-bold text-slate-800 dark:text-slate-200">
                          {gp.studentId?.firstName} {gp.studentId?.lastName} ({gp.studentId?.enrollmentNo})
                        </div>
                        <div className="text-slate-500 text-[11px]">
                          {gp.passType} • {gp.destination}
                        </div>
                      </div>
                      <span
                        className={`px-2.5 py-1 rounded-full font-bold text-[10px] ${
                          gp.status === "Approved"
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                            : gp.status === "Checked Out"
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                            : gp.status === "Applied"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                            : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        }`}
                      >
                        {gp.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-sm text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <FaTools className="text-indigo-500" /> Maintenance Tickets
                  </h3>
                  <button onClick={() => setSelectedMenu("Maintenance")} className="text-xs text-rose-500 hover:underline font-semibold cursor-pointer">
                    Manage Tickets ({complaints.length})
                  </button>
                </div>
                <div className="space-y-2">
                  {complaints.slice(0, 4).map((c) => (
                    <div key={c._id} className="flex justify-between items-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 text-xs">
                      <div>
                        <div className="font-bold text-slate-800 dark:text-slate-200">
                          #{c.ticketId} - {c.title}
                        </div>
                        <div className="text-slate-500 text-[11px]">
                          {c.category} • Priority: <span className="font-bold text-rose-500">{c.priority}</span>
                        </div>
                      </div>
                      <span
                        className={`px-2.5 py-1 rounded-full font-bold text-[10px] ${
                          c.status === "Resolved"
                            ? "bg-emerald-100 text-emerald-700"
                            : c.status === "In Progress"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {c.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case "Room & Bed Matrix":
        return (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-slate-500 uppercase">Select Block:</span>
                <div className="flex flex-wrap gap-2">
                  {blocks.map((b) => (
                    <button
                      key={b._id}
                      onClick={() => {
                        setSelectedBlock(b._id);
                        fetchRooms(b._id);
                      }}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold cursor-pointer transition ${
                        selectedBlock === b._id
                          ? "bg-rose-600 text-white shadow-xs"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200"
                      }`}
                    >
                      {b.blockName} ({b.type})
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {selectedBlock && selectedBlock !== "all" && (
                  <button
                    onClick={() => {
                      const currentBlockObj = blocks.find((b) => b._id === selectedBlock);
                      if (currentBlockObj) handleOpenEditBlock(currentBlockObj);
                    }}
                    className="px-3 py-2 rounded-xl text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer shadow-xs transition"
                  >
                    ✏️ Edit Block
                  </button>
                )}
                <button
                  onClick={() => setShowAllocateModal(true)}
                  className="px-3.5 py-2 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer shadow-xs transition"
                >
                  <FaUserCheck className="inline mr-1" /> Direct Bed Allocation
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rooms.map((room) => (
                <div
                  key={room._id}
                  className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-5 space-y-4 hover:border-rose-300 dark:hover:border-rose-900 transition"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-black text-base text-slate-900 dark:text-slate-100">
                          Room {room.roomNumber}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          Floor {room.floorNumber}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500 font-medium">
                        {room.roomType} • ₹{room.rentPerSemester?.toLocaleString()}/sem
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenEditRoom(room)}
                        className="px-2 py-1 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition cursor-pointer"
                        title="Edit Room Details"
                      >
                        ✏️ Edit
                      </button>
                      <span
                        className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                          room.status === "Full"
                            ? "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300"
                            : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                        }`}
                      >
                        {room.currentOccupancy}/{room.capacity} Beds
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {room.amenities?.map((am, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-md bg-slate-50 dark:bg-slate-800/80 text-slate-500 border border-slate-200/50 dark:border-slate-700/50">
                        {am}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      Beds in Room:
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {room.beds?.map((bed) => (
                        <div
                          key={bed._id}
                          onClick={() => setSelectedBedForDetail(bed)}
                          className={`p-2.5 rounded-xl border text-xs cursor-pointer transition flex flex-col justify-between ${
                            bed.isOccupied
                              ? "border-rose-200 bg-rose-50/60 dark:border-rose-900/50 dark:bg-rose-950/20"
                              : "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-950/20 hover:border-emerald-400"
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              {bed.bedLabel}
                            </span>
                            <span
                              className={`w-2 h-2 rounded-full ${
                                bed.isOccupied ? "bg-rose-500" : "bg-emerald-500"
                              }`}
                            />
                          </div>

                          {bed.isOccupied && bed.studentId ? (
                            <div className="mt-1">
                              <p className="font-bold text-[11px] text-rose-700 dark:text-rose-300 truncate">
                                {bed.studentId.firstName} {bed.studentId.lastName}
                              </p>
                              <p className="text-[10px] text-slate-500 truncate">
                                {bed.studentId.enrollmentNo}
                              </p>
                            </div>
                          ) : (
                            <div className="mt-1">
                              <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                                Available
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "Allocations":
        return (
          <div className="bg-white dark:bg-slate-900 p-5 md:p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-5">
            {/* Header and Quick Stats */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-2 border-b border-slate-100 dark:border-slate-800">
              <div>
                <h3 className="text-base md:text-lg font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <FaUserGraduate className="text-rose-500 text-lg" />
                  Student Allocations & Bed Roster
                  <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-300 border border-rose-200 dark:border-rose-900">
                    Showing {filteredAllocations.length} of {allocations.length}
                  </span>
                </h3>
                <p className="text-xs text-slate-500 font-medium mt-0.5">
                  Filter hostellers by Semester, Regulation, Batch, and Department with live billing reconciliation
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={() => {
                    setStudentLookupData(null);
                    setStudentLookupError("");
                    setShowAllocateModal(true);
                  }}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-gradient-to-r from-rose-500 to-amber-600 text-white hover:opacity-95 cursor-pointer shadow-md transition"
                >
                  <FaPlus /> Direct Bed Allocation
                </button>
              </div>
            </div>

            {/* Smart Filter Toolbar */}
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/80 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                  <FaFilter className="text-rose-500 text-[11px]" />
                  Filter Roster
                </span>
                {(allocSemesterFilter !== "all" ||
                  allocRegulationFilter !== "all" ||
                  allocBatchFilter !== "all" ||
                  allocBranchFilter !== "all" ||
                  allocStatusFilter !== "all" ||
                  allocSearch.trim()) && (
                  <button
                    onClick={() => {
                      setAllocSemesterFilter("all");
                      setAllocRegulationFilter("all");
                      setAllocBatchFilter("all");
                      setAllocBranchFilter("all");
                      setAllocStatusFilter("all");
                      setAllocSearch("");
                    }}
                    className="text-[11px] font-bold text-rose-600 hover:text-rose-700 dark:text-rose-400 flex items-center gap-1 cursor-pointer"
                  >
                    <FaRedo className="text-[10px]" /> Reset Filters
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 text-xs">
                {/* Semester */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1">
                    Semester
                  </label>
                  <select
                    value={allocSemesterFilter}
                    onChange={(e) => setAllocSemesterFilter(e.target.value)}
                    className="w-full p-2 rounded-xl border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 font-medium focus:ring-2 focus:ring-rose-500/20 focus:outline-hidden"
                  >
                    <option value="all">All Semesters</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                      <option key={s} value={s}>
                        Semester {s}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Regulation */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1">
                    Regulation
                  </label>
                  <select
                    value={allocRegulationFilter}
                    onChange={(e) => setAllocRegulationFilter(e.target.value)}
                    className="w-full p-2 rounded-xl border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 font-medium focus:ring-2 focus:ring-rose-500/20 focus:outline-hidden"
                  >
                    <option value="all">All Regulations</option>
                    {availableAllocRegulations.map((reg) => (
                      <option key={reg} value={reg}>
                        {reg}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Batch */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1">
                    Batch / Year
                  </label>
                  <select
                    value={allocBatchFilter}
                    onChange={(e) => setAllocBatchFilter(e.target.value)}
                    className="w-full p-2 rounded-xl border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 font-medium focus:ring-2 focus:ring-rose-500/20 focus:outline-hidden"
                  >
                    <option value="all">All Batches</option>
                    {availableAllocBatches.map((b) => (
                      <option key={b} value={b}>
                        Batch {b}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Branch */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1">
                    Branch
                  </label>
                  <select
                    value={allocBranchFilter}
                    onChange={(e) => setAllocBranchFilter(e.target.value)}
                    className="w-full p-2 rounded-xl border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 font-medium focus:ring-2 focus:ring-rose-500/20 focus:outline-hidden"
                  >
                    <option value="all">All Branches</option>
                    <option value="CSE">CSE</option>
                    <option value="ECE">ECE</option>
                    <option value="EEE">EEE</option>
                    <option value="MECH">MECH</option>
                    <option value="CIVIL">CIVIL</option>
                    <option value="IT">IT</option>
                    <option value="AI&DS">AI & DS</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1">
                    Status
                  </label>
                  <select
                    value={allocStatusFilter}
                    onChange={(e) => setAllocStatusFilter(e.target.value)}
                    className="w-full p-2 rounded-xl border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 font-medium focus:ring-2 focus:ring-rose-500/20 focus:outline-hidden"
                  >
                    <option value="all">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="Paid">Fee Paid</option>
                    <option value="Pending">Fee Pending</option>
                    <option value="Vacated">Vacated</option>
                  </select>
                </div>

                {/* Search */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1">
                    Search Hosteller
                  </label>
                  <div className="relative">
                    <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]" />
                    <input
                      type="text"
                      placeholder="Name, Roll, Bed..."
                      value={allocSearch}
                      onChange={(e) => setAllocSearch(e.target.value)}
                      className="w-full pl-7 pr-2.5 py-2 rounded-xl border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-400 font-medium focus:ring-2 focus:ring-rose-500/20 focus:outline-hidden"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Allocations Table */}
            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
              <table className="w-full text-left text-xs text-slate-600 dark:text-slate-400">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wider text-slate-700 dark:text-slate-300 font-bold border-b border-slate-200 dark:border-slate-800">
                  <tr>
                    <th className="p-3">Student Details</th>
                    <th className="p-3">Academic Info</th>
                    <th className="p-3">Block & Room</th>
                    <th className="p-3">Bed Code</th>
                    <th className="p-3">Fee Status</th>
                    <th className="p-3">Paid / Total</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredAllocations.length > 0 ? (
                    filteredAllocations.map((alloc) => {
                      const student = alloc.studentId || {};
                      const feeDue = Math.max(0, (alloc.feeStructure?.totalFee || 0) - (alloc.feeStructure?.paidAmount || 0));
                      return (
                        <tr key={alloc._id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition">
                          <td className="p-3">
                            <div className="font-bold text-slate-900 dark:text-slate-100 text-xs">
                              {student.firstName || "N/A"} {student.lastName || ""}
                            </div>
                            <div className="text-[11px] text-slate-500 font-mono">
                              {student.enrollmentNo || "N/A"}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap items-center gap-1">
                              {student.branch && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                  {student.branch}
                                </span>
                              )}
                              {student.semester != null && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                                  Sem {student.semester}
                                </span>
                              )}
                              {student.regulation && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                                  {student.regulation}
                                </span>
                              )}
                              {student.batch && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                                  '{String(student.batch).slice(-2)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 font-medium text-slate-800 dark:text-slate-200">
                            <div>{alloc.blockId?.blockName || "Hostel Block"}</div>
                            <div className="text-[11px] text-slate-400">
                              Room {alloc.roomId?.roomNumber || "-"} (Fl {alloc.roomId?.floorNumber || "0"})
                            </div>
                          </td>
                          <td className="p-3 font-mono font-bold text-rose-600 dark:text-rose-400">
                            {alloc.bedId?.bedCode || "-"}
                          </td>
                          <td className="p-3">
                            <span
                              className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                                alloc.feeStructure?.feeStatus === "Paid"
                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/80 dark:text-emerald-300"
                                  : "bg-amber-100 text-amber-700 dark:bg-amber-950/80 dark:text-amber-300"
                              }`}
                            >
                              {alloc.feeStructure?.feeStatus || "Pending"}
                            </span>
                            {feeDue > 0 && (
                              <div className="text-[10px] text-amber-600 dark:text-amber-400 font-mono mt-0.5">
                                Due: ₹{feeDue.toLocaleString()}
                              </div>
                            )}
                          </td>
                          <td className="p-3 font-semibold text-slate-800 dark:text-slate-200">
                            ₹{(alloc.feeStructure?.paidAmount || 0).toLocaleString()} / ₹{(alloc.feeStructure?.totalFee || 0).toLocaleString()}
                          </td>
                          <td className="p-3">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                alloc.status === "Active"
                                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                              }`}
                            >
                              {alloc.status}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            {alloc.status === "Active" && (
                              <button
                                onClick={() => {
                                  setSelectedAllocForVacate(alloc);
                                  setShowVacateModal(true);
                                }}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold text-rose-600 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 cursor-pointer transition"
                              >
                                Vacate & Settle
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-400">
                        <div className="text-sm font-semibold">No student allocations match the selected filters.</div>
                        <p className="text-xs mt-1">Try resetting the semester, regulation, or branch filters.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case "Gate Pass Center":
        return (
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <div>
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">
                Digital Gate Pass & Outing Action Center
              </h3>
              <p className="text-xs text-slate-500">
                Warden approvals, live out tracker, and overdue status
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {gatePasses.map((gp) => (
                <div
                  key={gp._id}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 space-y-3"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs text-rose-600">
                          #{gp.passNumber}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700">
                          {gp.passType}
                        </span>
                      </div>
                      <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100 mt-1">
                        {gp.studentId?.firstName} {gp.studentId?.lastName} ({gp.studentId?.enrollmentNo})
                      </h4>
                      <span className="text-xs text-slate-500">
                        {gp.studentId?.branch} • {gp.blockId?.blockName}
                      </span>
                    </div>

                    <span
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                        gp.status === "Approved"
                          ? "bg-emerald-100 text-emerald-700"
                          : gp.status === "Checked Out"
                          ? "bg-amber-100 text-amber-700"
                          : gp.status === "Applied"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      {gp.status}
                    </span>
                  </div>

                  <div className="bg-white dark:bg-slate-900 p-3 rounded-lg text-xs space-y-1">
                    <div className="text-slate-700 dark:text-slate-300">
                      <span className="font-bold">Destination:</span> {gp.destination}
                    </div>
                    <div className="text-slate-700 dark:text-slate-300">
                      <span className="font-bold">Reason:</span> {gp.reason}
                    </div>
                    <div className="text-slate-500 text-[11px] flex justify-between pt-1 border-t border-slate-100 dark:border-slate-800">
                      <span>Out: {new Date(gp.expectedOutTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      <span>In: {new Date(gp.expectedInTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>

                  {gp.status === "Applied" && (
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => handleGatePassAction(gp._id, "Approved", "Approved by Warden")}
                        className="flex-1 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer transition text-center"
                      >
                        <FaCheckCircle className="inline mr-1" /> Approve
                      </button>
                      <button
                        onClick={() => handleGatePassAction(gp._id, "Rejected", "Denied due to curfew")}
                        className="flex-1 py-1.5 rounded-lg text-xs font-bold bg-rose-600 text-white hover:bg-rose-700 cursor-pointer transition text-center"
                      >
                        <FaTimesCircle className="inline mr-1" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case "Roll Call": {
        // Compute stats for current rollCallSheet
        const totalSheetCount = rollCallSheet.length;
        const presentCount = rollCallSheet.filter((i) => i.status === "Present").length;
        const absentCount = rollCallSheet.filter((i) => i.status === "Absent").length;
        const onGatePassCount = rollCallSheet.filter((i) => i.status === "On GatePass" || i.onGatePass).length;
        const lateCount = rollCallSheet.filter((i) => i.status === "Late").length;
        const presentPercent = totalSheetCount > 0 ? Math.round((presentCount / totalSheetCount) * 100) : 0;

        // Filter by search and status
        const filteredSheet = rollCallSheet.filter((item) => {
          if (rollCallStatusFilter !== "all" && item.status !== rollCallStatusFilter) {
            return false;
          }
          if (rollCallSearch.trim()) {
            const q = rollCallSearch.toLowerCase().trim();
            const sName = `${item.student?.firstName || ""} ${item.student?.lastName || ""}`.toLowerCase();
            const sEnroll = (item.student?.enrollmentNo || "").toLowerCase();
            const rNum = (item.room?.roomNumber || "").toLowerCase();
            const bCode = (item.bed?.bedCode || "").toLowerCase();
            const bName = (item.block?.blockName || "").toLowerCase();
            return sName.includes(q) || sEnroll.includes(q) || rNum.includes(q) || bCode.includes(q) || bName.includes(q);
          }
          return true;
        });

        // Determine available floors for selected block
        const activeBlockObj = blocks.find((b) => b._id === rollCallBlock);
        const availableFloors = activeBlockObj?.totalFloors
          ? Array.from({ length: activeBlockObj.totalFloors }, (_, i) => i + 1)
          : [1, 2, 3, 4];

        return (
          <div className="space-y-5">
            {/* Top Command Banner */}
            <div className="bg-white dark:bg-slate-900 p-5 md:p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-rose-500 rounded-xl text-white shadow-md">
                  <FaClock className="text-xl" />
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    Night Roll Call & Curfew Monitoring
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                      Live Audit
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    Block-wise night attendance, automated gate pass reconciliation, and curfew verification
                  </p>
                </div>
              </div>

              {/* Date Selector & Save Action */}
              <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
                <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700">
                  <FaCalendarAlt className="text-slate-400 text-xs" />
                  <input
                    type="date"
                    value={rollCallDate}
                    onChange={(e) => {
                      setRollCallDate(e.target.value);
                      fetchRollCall(rollCallBlock, rollCallFloor, e.target.value);
                    }}
                    className="text-xs font-semibold bg-transparent text-slate-800 dark:text-slate-200 focus:outline-hidden cursor-pointer"
                  />
                </div>

                <button
                  onClick={handleSaveAttendance}
                  disabled={isSavingRollCall}
                  className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-gradient-to-r from-rose-500 to-amber-600 text-white hover:opacity-90 cursor-pointer shadow-md transition disabled:opacity-50"
                >
                  <FaCheckCircle /> {isSavingRollCall ? "Saving..." : "Save Night Roll Call"}
                </button>
              </div>
            </div>

            {/* Block Wise Selector Bar */}
            <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FaBuilding className="text-rose-500 text-xs" />
                  <span className="text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-400">
                    Select Block:
                  </span>
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>Curfew Time:</span>
                  <span className="font-bold text-amber-600 dark:text-amber-400">
                    {activeBlockObj ? `${activeBlockObj.curfewTime} PM` : "21:30 PM (Default)"}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  onClick={() => {
                    setRollCallBlock("all");
                    setRollCallFloor("all");
                    fetchRollCall("all", "all", rollCallDate);
                  }}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                    rollCallBlock === "all"
                      ? "bg-rose-600 text-white shadow-sm"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                  }`}
                >
                  🏢 All Blocks
                </button>

                {blocks.map((b) => (
                  <button
                    key={b._id}
                    onClick={() => {
                      setRollCallBlock(b._id);
                      setRollCallFloor("all");
                      fetchRollCall(b._id, "all", rollCallDate);
                    }}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                      rollCallBlock === b._id
                        ? "bg-rose-600 text-white shadow-sm"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    <span>{b.blockName}</span>
                    <span className="text-[10px] opacity-75">({b.type})</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Live Stats Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-5 gap-3">
              <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs space-y-1">
                <div className="flex justify-between items-center text-slate-500 text-xs font-semibold">
                  <span>Enrolled</span>
                  <FaUsers className="text-slate-400" />
                </div>
                <div className="text-xl font-black text-slate-900 dark:text-slate-100">{totalSheetCount}</div>
                <div className="text-[10px] text-slate-400">Total Hostellers</div>
              </div>

              <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-emerald-200/60 dark:border-emerald-950 shadow-xs space-y-1">
                <div className="flex justify-between items-center text-emerald-600 text-xs font-semibold">
                  <span>Present</span>
                  <FaCheckCircle />
                </div>
                <div className="text-xl font-black text-emerald-600 dark:text-emerald-400">
                  {presentCount} <span className="text-xs font-normal text-slate-400">({presentPercent}%)</span>
                </div>
                <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                  <div className="bg-emerald-500 h-full rounded-full transition-all duration-300" style={{ width: `${presentPercent}%` }} />
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-rose-200/60 dark:border-rose-950 shadow-xs space-y-1">
                <div className="flex justify-between items-center text-rose-600 text-xs font-semibold">
                  <span>Absent</span>
                  <FaTimesCircle />
                </div>
                <div className="text-xl font-black text-rose-600 dark:text-rose-400">{absentCount}</div>
                <div className="text-[10px] text-rose-500 font-medium">Unaccounted Hostellers</div>
              </div>

              <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-sky-200/60 dark:border-sky-950 shadow-xs space-y-1">
                <div className="flex justify-between items-center text-sky-600 text-xs font-semibold">
                  <span>On Gate Pass</span>
                  <FaPlaneDeparture />
                </div>
                <div className="text-xl font-black text-sky-600 dark:text-sky-400">{onGatePassCount}</div>
                <div className="text-[10px] text-sky-500 font-medium">Approved Leave / Outing</div>
              </div>

              <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-amber-200/60 dark:border-amber-950 shadow-xs space-y-1 col-span-2 md:col-span-1">
                <div className="flex justify-between items-center text-amber-600 text-xs font-semibold">
                  <span>Late Arrival</span>
                  <FaExclamationTriangle />
                </div>
                <div className="text-xl font-black text-amber-600 dark:text-amber-400">{lateCount}</div>
                <div className="text-[10px] text-amber-500 font-medium">Curfew Breaches</div>
              </div>
            </div>

            {/* Filter & Batch Actions Toolbar */}
            <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
              <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-3">
                {/* Search Bar & Floor Filter */}
                <div className="flex flex-wrap items-center gap-2.5 flex-1">
                  <div className="relative flex-1 min-w-[200px]">
                    <FaSearch className="absolute left-3.5 top-3 text-slate-400 text-xs" />
                    <input
                      type="text"
                      placeholder="Search hosteller by name, roll no, room, bed..."
                      value={rollCallSearch}
                      onChange={(e) => setRollCallSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 rounded-xl text-xs border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-hidden"
                    />
                    {rollCallSearch && (
                      <button
                        onClick={() => setRollCallSearch("")}
                        className="absolute right-3 top-2.5 text-xs text-slate-400 hover:text-slate-600"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* Floor Dropdown */}
                  <div className="flex items-center gap-1.5">
                    <FaFilter className="text-slate-400 text-xs" />
                    <select
                      value={rollCallFloor}
                      onChange={(e) => {
                        setRollCallFloor(e.target.value);
                        fetchRollCall(rollCallBlock, e.target.value, rollCallDate);
                      }}
                      className="px-3 py-2 rounded-xl text-xs font-semibold border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 cursor-pointer"
                    >
                      <option value="all">All Floors</option>
                      {availableFloors.map((fl) => (
                        <option key={fl} value={fl}>Floor {fl}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Batch Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleMarkAllStudents("Present")}
                    className="flex-1 lg:flex-none px-3 py-2 rounded-xl text-xs font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 border border-emerald-200/60 dark:border-emerald-800 cursor-pointer transition flex items-center justify-center gap-1"
                  >
                    <FaCheckDouble /> Mark All Present
                  </button>
                  <button
                    onClick={() => handleMarkAllStudents("Absent")}
                    className="flex-1 lg:flex-none px-3 py-2 rounded-xl text-xs font-bold bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 hover:bg-rose-100 border border-rose-200/60 dark:border-rose-800 cursor-pointer transition flex items-center justify-center gap-1"
                  >
                    <FaTimesCircle /> Mark All Absent
                  </button>
                  <button
                    onClick={() => fetchRollCall(rollCallBlock, rollCallFloor, rollCallDate)}
                    title="Reload from server"
                    className="p-2 rounded-xl text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 cursor-pointer transition"
                  >
                    <FaRedo />
                  </button>
                </div>
              </div>

              {/* Status Filter Tabs */}
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-800 text-xs">
                {[
                  { key: "all", label: "All Hostellers", count: totalSheetCount },
                  { key: "Present", label: "Present", count: presentCount },
                  { key: "Absent", label: "Absent", count: absentCount },
                  { key: "On GatePass", label: "On GatePass", count: onGatePassCount },
                  { key: "Late", label: "Late Arrival", count: lateCount },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setRollCallStatusFilter(tab.key)}
                    className={`px-3 py-1.5 rounded-xl font-bold transition cursor-pointer flex items-center gap-1.5 ${
                      rollCallStatusFilter === tab.key
                        ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-xs"
                        : "bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 hover:bg-slate-100"
                    }`}
                  >
                    <span>{tab.label}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-black/10 dark:bg-white/20">
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Students Attendance Grid */}
            {rollCallLoading ? (
              <div className="p-12 text-center text-xs text-slate-500">
                Loading Night Roll Call data...
              </div>
            ) : filteredSheet.length === 0 ? (
              <div className="bg-white dark:bg-slate-900 p-12 rounded-2xl border border-slate-200 dark:border-slate-800 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto text-slate-400 text-xl">
                  <FaUsers />
                </div>
                <h4 className="font-bold text-slate-800 dark:text-slate-200 text-sm">
                  No Hostellers Found
                </h4>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  No student records match the selected Block, Floor, or Search filter. Try changing your filter options.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredSheet.map((item, idx) => {
                  const sheetIndex = rollCallSheet.findIndex((s) => s.allocationId === item.allocationId);
                  return (
                    <div
                      key={item.allocationId || idx}
                      className={`p-4 rounded-2xl border transition shadow-xs flex flex-col justify-between space-y-3 ${
                        item.status === "Present"
                          ? "bg-white dark:bg-slate-900 border-emerald-200 dark:border-emerald-950/80"
                          : item.status === "Absent"
                          ? "bg-rose-50/40 dark:bg-rose-950/20 border-rose-300 dark:border-rose-900/60"
                          : item.status === "On GatePass" || item.onGatePass
                          ? "bg-sky-50/40 dark:bg-sky-950/20 border-sky-300 dark:border-sky-900/60"
                          : "bg-amber-50/40 dark:bg-amber-950/20 border-amber-300 dark:border-amber-900/60"
                      }`}
                    >
                      {/* Student Header */}
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-extrabold text-sm text-slate-900 dark:text-slate-100">
                            {item.student?.firstName} {item.student?.lastName}
                          </h4>
                          <span className="text-[11px] font-mono text-slate-500 block">
                            {item.student?.enrollmentNo} • {item.student?.branch || "CSE"}
                          </span>
                        </div>

                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-black ${
                            item.status === "Present"
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                              : item.status === "Absent"
                              ? "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300"
                              : item.status === "On GatePass" || item.onGatePass
                              ? "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300"
                              : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>

                      {/* Location & Bed Badges */}
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-400">
                        <span className="px-2 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 font-medium">
                          {item.block?.blockName}
                        </span>
                        <span className="px-2 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 font-bold">
                          Room {item.room?.roomNumber} (Fl. {item.room?.floorNumber || 1})
                        </span>
                        <span className="px-2 py-0.5 rounded-lg bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-300 font-mono font-bold">
                          {item.bed?.bedCode}
                        </span>
                      </div>

                      {/* Gate pass alert if applicable */}
                      {item.onGatePass && (
                        <div className="flex items-center gap-1.5 p-2 rounded-xl bg-sky-100/70 dark:bg-sky-950/50 text-sky-800 dark:text-sky-200 text-[11px] font-semibold">
                          <FaPlaneDeparture className="text-sky-600 shrink-0" />
                          <span>Approved Outing / Leave Verified</span>
                        </div>
                      )}

                      {/* Status Toggle Buttons */}
                      <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                        <div className="grid grid-cols-4 gap-1">
                          {[
                            { key: "Present", label: "Present", color: "bg-emerald-600 text-white" },
                            { key: "Absent", label: "Absent", color: "bg-rose-600 text-white" },
                            { key: "Late", label: "Late", color: "bg-amber-600 text-white" },
                            { key: "On GatePass", label: "Leave", color: "bg-sky-600 text-white" },
                          ].map((st) => (
                            <button
                              key={st.key}
                              type="button"
                              onClick={() => {
                                const updated = [...rollCallSheet];
                                if (sheetIndex !== -1) {
                                  updated[sheetIndex].status = st.key;
                                  setRollCallSheet(updated);
                                }
                              }}
                              className={`py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition text-center ${
                                item.status === st.key
                                  ? st.color + " shadow-xs"
                                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                              }`}
                            >
                              {st.label}
                            </button>
                          ))}
                        </div>

                        {/* Optional Remarks Input */}
                        <input
                          type="text"
                          placeholder="Add note (e.g. sick, library)..."
                          value={item.remarks || ""}
                          onChange={(e) => {
                            const updated = [...rollCallSheet];
                            if (sheetIndex !== -1) {
                              updated[sheetIndex].remarks = e.target.value;
                              setRollCallSheet(updated);
                            }
                          }}
                          className="w-full px-2.5 py-1 text-[10px] rounded-lg border border-slate-200/60 dark:border-slate-700/60 bg-slate-50/50 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 focus:outline-hidden"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      case "Mess & Dining":
        return (
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
            <div>
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">
                Hostel Dining & Weekly Mess Menu
              </h3>
              <p className="text-xs text-slate-500">
                Scheduled meal menus, dietary options, and counter tokens
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {messMenus.map((menu) => (
                <div
                  key={menu._id}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 space-y-3"
                >
                  <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-700">
                    <h4 className="font-bold text-sm text-rose-600 dark:text-rose-400">
                      {menu.dayOfWeek}
                    </h4>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                      Active
                    </span>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div>
                      <span className="font-bold text-slate-700 dark:text-slate-300">🥣 Breakfast:</span>
                      <p className="text-slate-500 text-[11px]">{menu.breakfast?.items?.join(", ")}</p>
                    </div>
                    <div>
                      <span className="font-bold text-slate-700 dark:text-slate-300">🍛 Lunch:</span>
                      <p className="text-slate-500 text-[11px]">{menu.lunch?.items?.join(", ")}</p>
                    </div>
                    <div>
                      <span className="font-bold text-slate-700 dark:text-slate-300">☕ Snacks:</span>
                      <p className="text-slate-500 text-[11px]">{menu.snacks?.items?.join(", ")}</p>
                    </div>
                    <div>
                      <span className="font-bold text-slate-700 dark:text-slate-300">🍲 Dinner:</span>
                      <p className="text-slate-500 text-[11px]">{menu.dinner?.items?.join(", ")}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "Maintenance":
        return (
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <div>
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">
                Hostel Maintenance & Repair Command Center
              </h3>
              <p className="text-xs text-slate-500">
                Technician assignment, SLA compliance, and resolution feedback
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {complaints.map((c) => (
                <div
                  key={c._id}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 space-y-3"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs text-indigo-600">
                          #{c.ticketId}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700">
                          {c.category}
                        </span>
                      </div>
                      <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100 mt-1">
                        {c.title}
                      </h4>
                      <span className="text-xs text-slate-500">
                        Raised by {c.studentId?.firstName} ({c.studentId?.enrollmentNo}) • Room {c.roomId?.roomNumber}
                      </span>
                    </div>

                    <span
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                        c.status === "Resolved"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-rose-100 text-rose-700"
                      }`}
                    >
                      {c.status}
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 p-2.5 rounded-lg">
                    {c.description}
                  </p>

                  {c.status !== "Resolved" && (
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(
                              `${baseApiURL()}/hostel/complaints/${c._id}`,
                              { status: "Resolved", resolutionNotes: "Repaired by Campus Maintenance Team" },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            toast.success("Ticket marked as Resolved!");
                            fetchInitialData();
                          } catch (err) {
                            toast.error("Failed to update ticket");
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer transition"
                      >
                        Mark as Resolved
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case "Gate Scanner":
        return <HostelGateScanner />;

      case "AI Assistant":
        return <HostelAIAssistant />;

      case "Reports":
        return <HostelReports />;

      case "Notices":
        return (
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">
                  Hostel Notices & Circulars
                </h3>
                <p className="text-xs text-slate-500">
                  Broadcast announcements to hostellers
                </p>
              </div>
              <button
                onClick={() => setShowNoticeModal(true)}
                className="px-3.5 py-2 text-xs font-bold rounded-xl bg-gradient-to-r from-rose-500 to-amber-600 text-white cursor-pointer shadow-xs"
              >
                <FaPlus className="inline mr-1" /> Publish Notice
              </button>
            </div>

            <div className="space-y-3">
              {notices.map((n) => (
                <div
                  key={n._id}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 space-y-2"
                >
                  <div className="flex justify-between items-start">
                    <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100">
                      {n.title}
                    </h4>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                      {n.priority}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300">
                    {n.description}
                  </p>
                  <div className="text-[11px] text-slate-400">
                    Issued by: {n.issuedBy} • Target: {n.targetAudience}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {load && (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-250">
          <Navbar />
          <Sidebar
            selectedMenu={selectedMenu}
            setSelectedMenu={setSelectedMenu}
            userType="Hostel"
            isCollapsed={isSidebarCollapsed}
            setIsCollapsed={setIsSidebarCollapsed}
          />

          {/* Main Content Area */}
          <div
            className={`transition-all duration-300 ${
              isSidebarCollapsed
                ? "md:ml-16 w-full md:w-[calc(100%-4rem)]"
                : "md:ml-64 w-full md:w-[calc(100%-16rem)]"
            } ml-0 min-h-[calc(100vh-4rem)]`}
          >
            <div className="p-4 md:p-6 lg:p-8 w-full space-y-6">
              {/* Header Banner */}
              <div className="rounded-2xl bg-white dark:bg-slate-900 p-5 md:p-6 border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-rose-500 to-amber-600 rounded-xl text-white shadow-md">
                    <FaBuilding className="text-2xl" />
                  </div>
                  <div>
                    <h1 className="text-xl md:text-2xl font-black bg-gradient-to-r from-rose-600 to-amber-600 bg-clip-text text-transparent">
                      Hostel Management & Security Hub
                    </h1>
                    <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 font-medium">
                      Smart Accommodation, Gate Passes, Mess Dining, Night Roll Call & AI Intelligence
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAddBlockModal(true)}
                    className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 cursor-pointer transition"
                  >
                    <FaPlus /> Add Block
                  </button>
                  <button
                    onClick={() => setShowAddRoomModal(true)}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-gradient-to-r from-rose-500 to-amber-600 text-white hover:opacity-90 cursor-pointer shadow-md transition"
                  >
                    <FaPlus /> Add Room
                  </button>
                </div>
              </div>

              {/* Render Selected View */}
              {renderContent()}
            </div>
          </div>

          {/* ADD BLOCK MODAL */}
          {showAddBlockModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">Add Hostel Block</h3>
                <form onSubmit={handleCreateBlock} className="space-y-3 text-xs">
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300">Block Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Visvesvaraya Block D"
                      value={newBlock.blockName}
                      onChange={(e) => setNewBlock({ ...newBlock, blockName: e.target.value })}
                      className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Code</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. VB-D"
                        value={newBlock.code}
                        onChange={(e) => setNewBlock({ ...newBlock, code: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Hostel Type</label>
                      <select
                        value={newBlock.type}
                        onChange={(e) => setNewBlock({ ...newBlock, type: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      >
                        <option value="Boys">Boys</option>
                        <option value="Girls">Girls</option>
                        <option value="Staff">Staff</option>
                        <option value="Guest">Guest</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowAddBlockModal(false)}
                      className="px-3 py-1.5 rounded-xl border text-xs font-bold cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-1.5 rounded-xl text-xs font-bold bg-rose-600 text-white cursor-pointer"
                    >
                      Save Block
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ADD ROOM MODAL */}
          {showAddRoomModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">Add Room & Generate Beds</h3>
                <form onSubmit={handleCreateRoom} className="space-y-3 text-xs">
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300">Hostel Block</label>
                    <select
                      required
                      value={newRoom.blockId}
                      onChange={(e) => setNewRoom({ ...newRoom, blockId: e.target.value })}
                      className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                    >
                      <option value="">Select Block</option>
                      {blocks.map((b) => (
                        <option key={b._id} value={b._id}>{b.blockName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Room Number</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. 101"
                        value={newRoom.roomNumber}
                        onChange={(e) => setNewRoom({ ...newRoom, roomNumber: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Floor Number</label>
                      <input
                        type="number"
                        min="0"
                        required
                        value={newRoom.floorNumber}
                        onChange={(e) => setNewRoom({ ...newRoom, floorNumber: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Room Type</label>
                      <select
                        value={newRoom.roomType}
                        onChange={(e) => setNewRoom({ ...newRoom, roomType: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      >
                        <option value="Single">Single (1 Bed)</option>
                        <option value="Double">Double (2 Beds)</option>
                        <option value="Triple">Triple (3 Beds)</option>
                        <option value="Quad">Quad (4 Beds)</option>
                        <option value="Deluxe AC">Deluxe AC</option>
                      </select>
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Capacity (Beds)</label>
                      <input
                        type="number"
                        min="1"
                        required
                        value={newRoom.capacity}
                        onChange={(e) => setNewRoom({ ...newRoom, capacity: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowAddRoomModal(false)}
                      className="px-3 py-1.5 rounded-xl border text-xs font-bold cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-1.5 rounded-xl text-xs font-bold bg-rose-600 text-white cursor-pointer"
                    >
                      Create Room
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* EDIT BLOCK MODAL */}
          {showEditBlockModal && selectedBlockForEdit && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-lg w-full border border-slate-200 dark:border-slate-800 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800">
                  <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                    Edit Hostel Block: {selectedBlockForEdit.blockName}
                  </h3>
                  <button
                    onClick={() => setShowEditBlockModal(false)}
                    className="text-slate-400 hover:text-slate-600 text-sm font-bold cursor-pointer"
                  >
                    ✕
                  </button>
                </div>
                <form onSubmit={handleUpdateBlock} className="space-y-3 text-xs">
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300">Block Name</label>
                    <input
                      type="text"
                      required
                      value={editBlockForm.blockName}
                      onChange={(e) => setEditBlockForm({ ...editBlockForm, blockName: e.target.value })}
                      className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Code</label>
                      <input
                        type="text"
                        required
                        value={editBlockForm.code}
                        onChange={(e) => setEditBlockForm({ ...editBlockForm, code: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Hostel Type</label>
                      <select
                        value={editBlockForm.type}
                        onChange={(e) => setEditBlockForm({ ...editBlockForm, type: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      >
                        <option value="Boys">Boys</option>
                        <option value="Girls">Girls</option>
                        <option value="Staff">Staff</option>
                        <option value="Guest">Guest</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Total Floors</label>
                      <input
                        type="number"
                        min="1"
                        required
                        value={editBlockForm.totalFloors}
                        onChange={(e) => setEditBlockForm({ ...editBlockForm, totalFloors: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Curfew Time (24h or AM/PM)</label>
                      <input
                        type="text"
                        value={editBlockForm.curfewTime}
                        onChange={(e) => setEditBlockForm({ ...editBlockForm, curfewTime: e.target.value })}
                        placeholder="e.g. 21:30"
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Caretaker / Warden Name</label>
                      <input
                        type="text"
                        value={editBlockForm.caretakerName}
                        onChange={(e) => setEditBlockForm({ ...editBlockForm, caretakerName: e.target.value })}
                        placeholder="e.g. Mr. Rajesh Sharma"
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Caretaker Phone</label>
                      <input
                        type="text"
                        value={editBlockForm.caretakerPhone}
                        onChange={(e) => setEditBlockForm({ ...editBlockForm, caretakerPhone: e.target.value })}
                        placeholder="e.g. +91 9876543210"
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Block Status</label>
                      <select
                        value={editBlockForm.status}
                        onChange={(e) => setEditBlockForm({ ...editBlockForm, status: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      >
                        <option value="Active">Active</option>
                        <option value="Maintenance">Maintenance</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Description / Note</label>
                      <input
                        type="text"
                        value={editBlockForm.description}
                        onChange={(e) => setEditBlockForm({ ...editBlockForm, description: e.target.value })}
                        placeholder="e.g. Near North Gate"
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <button
                      type="button"
                      onClick={() => setShowEditBlockModal(false)}
                      className="px-3.5 py-1.5 rounded-xl border text-xs font-bold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-1.5 rounded-xl text-xs font-bold bg-rose-600 text-white hover:bg-rose-700 cursor-pointer shadow-sm transition"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* EDIT ROOM MODAL */}
          {showEditRoomModal && selectedRoomForEdit && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-lg w-full border border-slate-200 dark:border-slate-800 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800">
                  <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                    Edit Room {selectedRoomForEdit.roomNumber}
                  </h3>
                  <button
                    onClick={() => setShowEditRoomModal(false)}
                    className="text-slate-400 hover:text-slate-600 text-sm font-bold cursor-pointer"
                  >
                    ✕
                  </button>
                </div>
                <form onSubmit={handleUpdateRoom} className="space-y-3 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Room Number</label>
                      <input
                        type="text"
                        required
                        value={editRoomForm.roomNumber}
                        onChange={(e) => setEditRoomForm({ ...editRoomForm, roomNumber: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Floor Number</label>
                      <input
                        type="number"
                        min="0"
                        required
                        value={editRoomForm.floorNumber}
                        onChange={(e) => setEditRoomForm({ ...editRoomForm, floorNumber: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Room Type</label>
                      <select
                        value={editRoomForm.roomType}
                        onChange={(e) => setEditRoomForm({ ...editRoomForm, roomType: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      >
                        <option value="Single">Single (1 Bed)</option>
                        <option value="Double">Double (2 Beds)</option>
                        <option value="Triple">Triple (3 Beds)</option>
                        <option value="Quad">Quad (4 Beds)</option>
                        <option value="Deluxe AC">Deluxe AC</option>
                      </select>
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Capacity (Beds)</label>
                      <input
                        type="number"
                        min="1"
                        required
                        value={editRoomForm.capacity}
                        onChange={(e) => setEditRoomForm({ ...editRoomForm, capacity: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                      <p className="text-[10px] text-slate-400 mt-0.5">Increasing will automatically generate new beds.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Rent / Semester (₹)</label>
                      <input
                        type="number"
                        min="0"
                        required
                        value={editRoomForm.rentPerSemester}
                        onChange={(e) => setEditRoomForm({ ...editRoomForm, rentPerSemester: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300">Room Status</label>
                      <select
                        value={editRoomForm.status}
                        onChange={(e) => setEditRoomForm({ ...editRoomForm, status: e.target.value })}
                        className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      >
                        <option value="Available">Available</option>
                        <option value="Full">Full</option>
                        <option value="Maintenance">Maintenance</option>
                        <option value="Blocked">Blocked</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300">Amenities (comma-separated)</label>
                    <input
                      type="text"
                      value={editRoomForm.amenities}
                      onChange={(e) => setEditRoomForm({ ...editRoomForm, amenities: e.target.value })}
                      placeholder="e.g. Attached Bath, AC, Balcony, WiFi"
                      className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300">Special Notes / Inspection</label>
                    <textarea
                      rows="2"
                      value={editRoomForm.notes}
                      onChange={(e) => setEditRoomForm({ ...editRoomForm, notes: e.target.value })}
                      placeholder="Optional notes about room condition..."
                      className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <button
                      type="button"
                      onClick={() => setShowEditRoomModal(false)}
                      className="px-3.5 py-1.5 rounded-xl border text-xs font-bold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-1.5 rounded-xl text-xs font-bold bg-rose-600 text-white hover:bg-rose-700 cursor-pointer shadow-sm transition"
                    >
                      Save Room Details
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ALLOCATE BED MODAL */}
          {showAllocateModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-lg w-full border border-slate-200 dark:border-slate-800 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800">
                  <div>
                    <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                      <FaUserGraduate className="text-rose-500" /> Allocate Student to Bed
                    </h3>
                    <p className="text-xs text-slate-500">
                      Verify student academic profile and assign room & bed inventory
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowAllocateModal(false);
                      setStudentLookupData(null);
                      setStudentLookupError("");
                    }}
                    className="text-slate-400 hover:text-slate-600 text-sm font-bold cursor-pointer"
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleAllocateBed} className="space-y-3.5 text-xs">
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">
                      Student Enrollment Number
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        required
                        placeholder="e.g. 22N81A0502"
                        value={allocForm.enrollmentNo}
                        onChange={(e) => {
                          setAllocForm({ ...allocForm, enrollmentNo: e.target.value });
                          if (studentLookupData) setStudentLookupData(null);
                        }}
                        onBlur={() => {
                          if (allocForm.enrollmentNo?.trim()) {
                            lookupStudent(allocForm.enrollmentNo);
                          }
                        }}
                        className="flex-1 p-2.5 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700 font-mono text-slate-800 dark:text-slate-200 uppercase focus:ring-2 focus:ring-rose-500/20 focus:outline-hidden"
                      />
                      <button
                        type="button"
                        onClick={() => lookupStudent(allocForm.enrollmentNo)}
                        disabled={studentLookupLoading || !allocForm.enrollmentNo?.trim()}
                        className="px-3.5 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold border border-slate-200 dark:border-slate-700 cursor-pointer disabled:opacity-50 transition"
                      >
                        {studentLookupLoading ? "Fetching..." : "Fetch"}
                      </button>
                    </div>
                  </div>

                  {/* Student Verification & Academic Preview Card */}
                  {studentLookupLoading && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl flex items-center gap-2 text-xs text-slate-500">
                      <div className="w-3.5 h-3.5 border-2 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
                      Fetching student details from academic records...
                    </div>
                  )}

                  {studentLookupError && (
                    <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 rounded-xl text-rose-600 dark:text-rose-400 text-xs">
                      {studentLookupError}
                    </div>
                  )}

                  {studentLookupData && (
                    <div className="p-3.5 rounded-xl bg-gradient-to-br from-indigo-50/80 to-rose-50/80 dark:from-indigo-950/30 dark:to-rose-950/30 border border-indigo-100 dark:border-indigo-900/50 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="font-bold text-slate-800 dark:text-slate-100 text-xs flex items-center gap-1.5">
                          <FaUserGraduate className="text-indigo-600 dark:text-indigo-400" />
                          {studentLookupData.firstName} {studentLookupData.middleName || ""} {studentLookupData.lastName}
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900">
                          Verified Student
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[10px]">
                        <div className="p-1.5 bg-white dark:bg-slate-900 rounded-lg border border-slate-200/60 dark:border-slate-800">
                          <span className="text-slate-400 block font-medium">Branch</span>
                          <span className="font-bold text-slate-700 dark:text-slate-200">{studentLookupData.branch || "-"}</span>
                        </div>
                        <div className="p-1.5 bg-white dark:bg-slate-900 rounded-lg border border-slate-200/60 dark:border-slate-800">
                          <span className="text-slate-400 block font-medium">Semester</span>
                          <span className="font-bold text-indigo-600 dark:text-indigo-400">Sem {studentLookupData.semester != null ? studentLookupData.semester : "-"}</span>
                        </div>
                        <div className="p-1.5 bg-white dark:bg-slate-900 rounded-lg border border-slate-200/60 dark:border-slate-800">
                          <span className="text-slate-400 block font-medium">Regulation</span>
                          <span className="font-bold text-purple-600 dark:text-purple-400">{studentLookupData.regulation || "-"}</span>
                        </div>
                        <div className="p-1.5 bg-white dark:bg-slate-900 rounded-lg border border-slate-200/60 dark:border-slate-800">
                          <span className="text-slate-400 block font-medium">Batch</span>
                          <span className="font-bold text-amber-600 dark:text-amber-400">{studentLookupData.batch || "-"}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Select Available Bed</label>
                    <select
                      required
                      value={allocForm.bedId}
                      onChange={(e) => {
                        const selectedBId = e.target.value;
                        const parentRoom = rooms.find((r) => r.beds?.some((b) => b._id === selectedBId));
                        setAllocForm((prev) => ({
                          ...prev,
                          bedId: selectedBId,
                          roomRent: parentRoom?.rentPerSemester != null ? parentRoom.rentPerSemester : prev.roomRent,
                        }));
                      }}
                      className="w-full p-2.5 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700 font-mono text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:outline-hidden"
                    >
                      <option value="">-- Choose Available Bed --</option>
                      {rooms.flatMap((r) => r.beds || []).filter((b) => !b.isOccupied).map((b) => (
                        <option key={b._id} value={b._id}>
                          {b.bedCode} ({b.bedLabel}) - ₹{rooms.find(r => r._id === b.roomId)?.rentPerSemester?.toLocaleString() || '25,000'}/sem
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Hostel Fee (₹)</label>
                      <input
                        type="number"
                        min="0"
                        value={allocForm.roomRent}
                        onChange={(e) => setAllocForm({ ...allocForm, roomRent: e.target.value })}
                        className="w-full p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700 text-slate-800 dark:text-slate-200"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Paid Amount (₹)</label>
                      <input
                        type="number"
                        min="0"
                        value={allocForm.paidAmount}
                        onChange={(e) => setAllocForm({ ...allocForm, paidAmount: e.target.value })}
                        className="w-full p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700 text-slate-800 dark:text-slate-200"
                      />
                    </div>
                  </div>

                  {/* Fee Summary Preview */}
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 space-y-1 text-[11px]">
                    <div className="flex justify-between font-bold text-slate-700 dark:text-slate-300">
                      <span>Total Charge:</span>
                      <span className="text-rose-600 dark:text-rose-400 font-mono">
                        ₹{Number(allocForm.roomRent || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>Paid Today:</span>
                      <span className="font-mono text-emerald-600 dark:text-emerald-400">
                        ₹{Number(allocForm.paidAmount || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between font-bold text-slate-700 dark:text-slate-300 border-t border-slate-200 dark:border-slate-700 pt-1">
                      <span>Remaining Balance Due:</span>
                      <span className="font-mono text-amber-600 dark:text-amber-400">
                        ₹{Math.max(0, Number(allocForm.roomRent || 0) - Number(allocForm.paidAmount || 0)).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAllocateModal(false);
                        setStudentLookupData(null);
                        setStudentLookupError("");
                      }}
                      className="px-3.5 py-2 rounded-xl border text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-rose-500 to-amber-600 text-white hover:opacity-95 shadow-md cursor-pointer transition"
                    >
                      Confirm Allocation
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* VACATE MODAL */}
          {showVacateModal && selectedAllocForVacate && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                  Hostel Clearance & Vacate Bed
                </h3>
                <p className="text-xs text-slate-500">
                  Student: {selectedAllocForVacate.studentId?.firstName} ({selectedAllocForVacate.studentId?.enrollmentNo})
                </p>
                <form onSubmit={handleVacateSubmit} className="space-y-3 text-xs">
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300">Room Damage Deductions (₹)</label>
                    <input
                      type="number"
                      min="0"
                      value={vacateForm.damagesCost}
                      onChange={(e) => setVacateForm({ ...vacateForm, damagesCost: e.target.value })}
                      className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300">Remarks / Inspection Notes</label>
                    <textarea
                      rows="2"
                      value={vacateForm.remarks}
                      onChange={(e) => setVacateForm({ ...vacateForm, remarks: e.target.value })}
                      className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                      placeholder="Inventory in good condition..."
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowVacateModal(false)}
                      className="px-3 py-1.5 rounded-xl border text-xs font-bold cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-1.5 rounded-xl text-xs font-bold bg-rose-600 text-white cursor-pointer"
                    >
                      Complete Checkout
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* BED DETAIL / MANAGE MODAL */}
          {selectedBedForDetail && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800">
                  <div>
                    <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                      Bed Details: {selectedBedForDetail.bedCode}
                    </h3>
                    <p className="text-xs text-slate-500 font-medium">
                      Label: {selectedBedForDetail.bedLabel}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedBedForDetail(null)}
                    className="text-slate-400 hover:text-slate-600 text-sm font-bold cursor-pointer"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-3 text-xs">
                  <div className="flex justify-between items-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                    <span className="font-bold text-slate-600 dark:text-slate-400">Current Status:</span>
                    <span
                      className={`px-2.5 py-1 rounded-full font-bold text-[11px] ${
                        selectedBedForDetail.isOccupied
                          ? "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300"
                          : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                      }`}
                    >
                      {selectedBedForDetail.isOccupied ? "Occupied" : selectedBedForDetail.status || "Available"}
                    </span>
                  </div>

                  {selectedBedForDetail.isOccupied && selectedBedForDetail.studentId ? (
                    <div className="p-4 rounded-xl border border-rose-200 bg-rose-50/50 dark:border-rose-900/50 dark:bg-rose-950/20 space-y-2">
                      <h4 className="font-bold text-rose-800 dark:text-rose-300 text-xs uppercase tracking-wider">
                        Allocated Student Profile
                      </h4>
                      <div className="space-y-1 text-slate-700 dark:text-slate-300">
                        <p className="font-bold text-sm">
                          {selectedBedForDetail.studentId.firstName} {selectedBedForDetail.studentId.lastName}
                        </p>
                        <p className="text-[11px] text-slate-500 font-mono">
                          Enrollment: {selectedBedForDetail.studentId.enrollmentNo}
                        </p>
                        {selectedBedForDetail.studentId.branch && (
                          <p className="text-[11px] text-slate-500">
                            Branch: {selectedBedForDetail.studentId.branch}
                          </p>
                        )}
                        {selectedBedForDetail.studentId.phoneNumber && (
                          <p className="text-[11px] text-slate-500">
                            Phone: {selectedBedForDetail.studentId.phoneNumber}
                          </p>
                        )}
                      </div>

                      <div className="pt-2">
                        <button
                          type="button"
                          onClick={() => handleDeallocateBed(selectedBedForDetail._id)}
                          className="w-full py-2 rounded-xl text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white cursor-pointer shadow-sm transition"
                        >
                          Remove Allocation / Vacate Bed
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-xs text-slate-500">
                        This bed is currently unallocated and open for new hosteller registration.
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setAllocForm((prev) => ({ ...prev, bedId: selectedBedForDetail._id }));
                            setSelectedBedForDetail(null);
                            setShowAllocateModal(true);
                          }}
                          className="flex-1 py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer shadow-sm transition text-center"
                        >
                          Allocate Student
                        </button>
                        <button
                          type="button"
                          onClick={() => handleChangeBedStatus(selectedBedForDetail._id, selectedBedForDetail.status === "Blocked" ? "Available" : "Blocked")}
                          className="px-3 py-2 rounded-xl text-xs font-bold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition"
                        >
                          {selectedBedForDetail.status === "Blocked" ? "Unblock Bed" : "Block Bed"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-2 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => setSelectedBedForDetail(null)}
                    className="px-4 py-1.5 rounded-xl border text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* PUBLISH NOTICE MODAL */}
          {showNoticeModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">Publish Notice</h3>
                <form onSubmit={handleCreateNotice} className="space-y-3 text-xs">
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300">Notice Title</label>
                    <input
                      type="text"
                      required
                      value={noticeForm.title}
                      onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
                      className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300">Description</label>
                    <textarea
                      rows="3"
                      required
                      value={noticeForm.description}
                      onChange={(e) => setNoticeForm({ ...noticeForm, description: e.target.value })}
                      className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowNoticeModal(false)}
                      className="px-3 py-1.5 rounded-xl border text-xs font-bold cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-1.5 rounded-xl text-xs font-bold bg-rose-600 text-white cursor-pointer"
                    >
                      Publish
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
      <Toaster position="bottom-center" />
    </>
  );
};

export default HostelHome;
